import * as React from 'react';
import {Trans, useTranslation} from '@deviceinsight/ng-ui-scale-lib/api';
import {Checkbox} from '@deviceinsight/ng-ui-basic-components';

import type {UiRule} from '../../../modules/ruleengine/types';
import {OwnSubtenant} from '../../../modules/subtenants/types';
import InfoText from '../../common/InfoText';

import './RuleApiIntegrationContainer.less';
import {ACTION_TYPE_WEBSOCKET} from '../../../modules/ruleengine/config/consts';

type Props = {
	title: React.ReactNode;
	apiTriggerOnChange: (arg0: boolean) => void;
	websocketActionOnChange: (arg0: boolean) => void;
	ownSubtenant: OwnSubtenant | undefined;
	rule: UiRule | undefined;
};

const RuleApiIntegrationContainer: React.FC<Props> = ({
	title,
	ownSubtenant,
	rule,
	apiTriggerOnChange,
	websocketActionOnChange
}) => {
	const {t} = useTranslation();
	const [apiTriggerIsActive, setApiTriggerIsActive] = React.useState(false);
	const [websocketActionIsActive, setWebsocketActionIsActive] = React.useState(false);

	React.useEffect(() => {
		let hasWebsocketAction = false;
		rule?.actions.forEach((action) => {
			if (action.actionType === ACTION_TYPE_WEBSOCKET) {
				hasWebsocketAction = true;
			}
		});

		setApiTriggerIsActive(rule?.apiTriggerEnabled ?? false);
		setWebsocketActionIsActive(hasWebsocketAction);
	}, [rule]);

	const linkToDocumentation = t('ruleengine.edit.rule.apiIntegration.documentationLink', {
		defaultValue: 'https://docs.kisme.com/'
	});
	const linkToDocumentationDisplayName = t('ruleengine.edit.rule.apiIntegration.documentationLinkDisplayName', {
		defaultValue: 'KIS.API Documentation'
	});

	const handleApiTriggerChange = React.useCallback(() => {
		setApiTriggerIsActive(!apiTriggerIsActive);
		apiTriggerOnChange(!apiTriggerIsActive);
	}, [apiTriggerIsActive, apiTriggerOnChange]);

	const handleWebsocketActionChange = React.useCallback(() => {
		setWebsocketActionIsActive(!websocketActionIsActive);
		websocketActionOnChange(!websocketActionIsActive);
	}, [websocketActionIsActive, websocketActionOnChange]);

	return (
		<div className="rule-action-container">
			<h3>{title}</h3>
			{ownSubtenant?.kisApiEnabled && (
				<div className="api-integration-infotext">
					<Trans i18nKey="ruleengine.edit.rule.apiIntegration.infoKisApiEnabled">
						This allows to trigger a rule by a third-party system and generate Socket.IO events.
					</Trans>
				</div>
			)}
			{!ownSubtenant?.kisApiEnabled && (
				<div className="api-integration-infotext">
					<Trans i18nKey="ruleengine.edit.rule.apiIntegration.infoKisApiDisabled">
						This allows to trigger a rule by a third-party system and generate Socket.IO events. A KIS.API
						subscription is required.
					</Trans>
				</div>
			)}
			{rule?.id && (
				<div className="api-integration-rule-id">
					<Trans i18nKey="ruleengine.edit.rule.apiIntegration.ruleID">Rule ID: </Trans>
					<div className="api-integration-rule-id-field">{rule.id}</div>
				</div>
			)}
			<div className="api-integration-infotext">
				<label>
					<a href={linkToDocumentation} target="_blank" rel="noopener noreferrer">
						{linkToDocumentationDisplayName}
					</a>
				</label>
			</div>
			<div className="api-integration-rule-id">
				<Checkbox
					type="toggle"
					value={apiTriggerIsActive}
					toggleSmall
					disabled={!ownSubtenant?.kisApiEnabled}
					onChange={handleApiTriggerChange}
				/>
				<div className="api-integration-api-trigger-toggle-label">
					<Trans i18nKey="ruleengine.edit.rule.apiIntegration.apiTriggerToggle">API-Trigger active</Trans>
				</div>
				<div className="api-integration-api-trigger-toggle-tooltip">
					<InfoText
						bubble={{
							id: 'ruleengine.edit.rule.apiIntegration.apiTriggerToggleTooltip',
							defaultValue:
								'When the API-Trigger is active then a third-party system can trigger this rule'
						}}>
						&nbsp;
					</InfoText>
				</div>
			</div>

			<div className="api-integration-rule-id">
				<Checkbox
					type="toggle"
					value={websocketActionIsActive}
					toggleSmall
					disabled={!ownSubtenant?.kisApiEnabled}
					onChange={handleWebsocketActionChange}
				/>
				<div className="api-integration-api-trigger-toggle-label">
					<Trans i18nKey="ruleengine.edit.rule.apiIntegration.socketIoActionToggle">Socket.IO-Action</Trans>
				</div>
				<div className="api-integration-api-trigger-toggle-tooltip">
					<InfoText
						bubble={{
							id: 'ruleengine.edit.rule.apiIntegration.socketIoActionToggleTooltip',
							defaultValue:
								'When the Socket.IO Action is active then a Socket.IO action will be generated as an additional action'
						}}>
						&nbsp;
					</InfoText>
				</div>
			</div>
		</div>
	);
};

export default RuleApiIntegrationContainer;
